import "leaflet/dist/leaflet.css";
import React, { useEffect, useRef } from "react";
import L from "leaflet";
import { formatUsd } from "../lib/currencyDisplay";

// Vanilla Leaflet (avoids react-leaflet StrictMode double-init bug).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const REDPIN_HTML = `
  <svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.27 0 0 6.27 0 14c0 9.4 14 22 14 22s14-12.6 14-22C28 6.27 21.73 0 14 0z" fill="#E11D48"/>
    <circle cx="14" cy="14" r="5" fill="white"/>
  </svg>`;

export default function ParkingMap({ spots, height = 540 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    // Tear down any previous map (for StrictMode double-mount safety)
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const valid = (spots || []).filter(s => typeof s.lat === "number" && typeof s.lng === "number");
    const center = valid.length
      ? [valid.reduce((a, s) => a + s.lat, 0) / valid.length,
         valid.reduce((a, s) => a + s.lng, 0) / valid.length]
      : [39.5, -98.35];
    const zoom = valid.length === 1 ? 13 : 4;

    const map = L.map(ref.current, { scrollWheelZoom: true }).setView(center, zoom);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const icon = L.divIcon({
      className: "zc-map-pin",
      html: `<div style="display:flex;align-items:center;justify-content:center;">${REDPIN_HTML}</div>`,
      iconSize: [28, 36],
      iconAnchor: [14, 36],
      popupAnchor: [0, -32],
    });

    valid.forEach(s => {
      const m = L.marker([s.lat, s.lng], { icon }).addTo(map);
      const html = `
        <div style="min-width:200px">
          <div style="font-weight:600;margin-bottom:4px">${s.name}</div>
          <div style="font-size:12px;color:#5C5C66;margin-bottom:6px">${s.city} · $${formatUsd(s.price_per_hour)}/hr</div>
          <a href="/parking/${s.spot_id}" style="display:inline-block;padding:6px 12px;background:#E11D48;color:white;border-radius:6px;font-size:12px;font-weight:600;text-decoration:none">Book Space</a>
        </div>`;
      m.bindPopup(html);
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [spots]);

  return (
    <div className="zc-card overflow-hidden" data-testid="parking-map">
      <div ref={ref} style={{ height: `${height}px`, width: "100%" }} />
    </div>
  );
}
